import { Box } from '@chakra-ui/layout';

import { Text } from '@revelio/ui';

import { useDataBuilderQuery } from '../api/useDataBuilderQuery';
import {
  selectDatasetInstances,
  useDataBuilderSelector,
} from '../data-builder.store';
import type { DataBuilderDataset } from '../datasets/api-transform';
import {
  DATA_BUILDER_STEPS,
  DataBuilderPaths,
  type DataBuilderStepConfig,
} from '../steps/data-builder-steps';
import { Stepper } from './ui/Stepper';

const buildDatasetStepDescription = (labels: string[]) => {
  if (labels.length === 0) {
    return undefined;
  }
  if (labels.length > 10) {
    return `${labels.length} selected`;
  }
  return (
    <Box mt={1} ml={1}>
      {labels.map((label, i) => (
        <Text
          key={`${label}-${i}`}
          fontSize="x-sm"
          weight="regular"
          includePadding
        >
          {label}
        </Text>
      ))}
    </Box>
  );
};

const buildLabelMap = (datasets: DataBuilderDataset[]) => {
  const map: Record<string, string> = {};
  for (const ds of datasets) {
    map[ds.id] = ds.label;
  }
  return map;
};

const useStepsWithDescriptions = (): DataBuilderStepConfig[] => {
  const { data } = useDataBuilderQuery();
  const instances = useDataBuilderSelector(selectDatasetInstances);

  const labelMap = data !== undefined ? buildLabelMap(data.datasets) : {};

  const countByKey: Record<string, number> = {};
  for (const inst of instances) {
    countByKey[inst.datasetKey] = (countByKey[inst.datasetKey] ?? 0) + 1;
  }
  const selectedLabels = Object.entries(countByKey).map(([key, count]) => {
    const label = labelMap[key] ?? key;
    return count > 1 ? `${label} (\u00d7${count})` : label;
  });

  const datasetDescription = buildDatasetStepDescription(selectedLabels);

  return DATA_BUILDER_STEPS.map((step) => {
    if (step.path !== DataBuilderPaths.DATASET || !datasetDescription) {
      return step;
    }
    return { ...step, description: datasetDescription };
  });
};

type DataBuilderProgressSidebarProps = {
  currentStepIndex: number;
};

export const DataBuilderProgressSidebar = ({
  currentStepIndex,
}: DataBuilderProgressSidebarProps) => {
  const steps = useStepsWithDescriptions();

  const stepperSteps = steps.map((step, index) => ({
    id: step.id,
    title: step.title,
    details: step.description,
    isActive: index === currentStepIndex,
    isComplete: index < currentStepIndex,
  }));

  return <Stepper steps={stepperSteps} />;
};
